import { useCallback, useRef, useState } from 'react';

/**
 * `form`: the dialog holds inputs — the backdrop never closes it and a dirty
 * form asks "Discard changes?" before Cancel / × / Escape close it.
 * `confirm`: no inputs — backdrop click and Escape close at once.
 */
export type DialogKind = 'form' | 'confirm';

interface DismissOptions {
  kind?: DialogKind;
  /** The form differs from its initial values (see useIsDirty). */
  dirty?: boolean;
  /** A request is in flight: nothing closes the dialog. */
  busy?: boolean;
  onClose: () => void;
}

export interface DialogDismiss {
  kind: DialogKind;
  busy: boolean;
  onClose: () => void;
  /** For Cancel / × buttons and Escape. Successful submits call onClose directly. */
  requestClose: () => void;
  confirming: boolean;
  setConfirming: (value: boolean) => void;
}

export function useDialogDismiss({
  kind = 'form',
  dirty = false,
  busy = false,
  onClose,
}: DismissOptions): DialogDismiss {
  const [confirming, setConfirming] = useState(false);
  const requestClose = useCallback(() => {
    if (busy) return;
    if (kind === 'form' && dirty) setConfirming(true);
    else onClose();
  }, [busy, kind, dirty, onClose]);
  return { kind, busy, onClose, requestClose, confirming, setConfirming };
}

function same(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a instanceof File || b instanceof File) return false;
  if (a && b && typeof a === 'object' && typeof b === 'object')
    return JSON.stringify(a) === JSON.stringify(b);
  return false;
}

/** True once any value differs from the snapshot taken on first render (or when resetKey last changed). */
export function useIsDirty(values: Record<string, unknown>, resetKey?: unknown): boolean {
  const snapshot = useRef<{ key: unknown; values: Record<string, unknown> } | null>(null);
  if (snapshot.current === null || !Object.is(snapshot.current.key, resetKey)) {
    snapshot.current = { key: resetKey, values };
  }
  const initial = snapshot.current.values;
  return Object.keys(values).some((k) => !same(values[k], initial[k]));
}
